import { useMemo } from 'react';
import { useBasePaymentOptions } from '~/features/checkout/components/Payment/hooks';
import useCustomer from '~/features/commerce-api/hooks/useCustomer';
import { usePDPData } from '~/features/product-details/context';
import { SalesforceProductPrice, SalesforceProductPromotionType } from '~/lib/salesforce/types';
import { AltaPayPaymentType } from '~/services/altapay/payment';
import { useFrame } from '~/shared/utils';
import { ascertainNumber } from '~/shared/utils/numbers';
import styles from './ProductPaymentMethods.module.scss';
import clsx from 'clsx';

type Props = React.HTMLAttributes<HTMLDivElement>;

const PaymentMethodIcon: React.FC<{ name: string; src: string }> = ({ name, src }) => (
    <img className={styles.paymentIcon} src={src} alt={name} title={name} />
);

export const ProductPaymentMethods: React.FC<Props> = ({ className }) => {
    const {
        data: { productSettings, settings: siteSettings },
    } = useFrame();
    const { isPlusMember } = useCustomer();
    const { product } = usePDPData();
    const { options } = useBasePaymentOptions();

    const isKlarnaInPaymentOptions = options.some(
        (option) => option.id === AltaPayPaymentType.KLARNA,
    );

    const assumedPrice = useMemo(() => {
        const activePrices = product.c_prices?.filter(
            (price: SalesforceProductPrice) => price.type !== 'disabled',
        );

        // Regular, Tilbud, Superpris, Julpris prices
        if (activePrices?.length === 1) return activePrices[0]?.value;

        const memberPrice = activePrices?.find(
            (price: SalesforceProductPrice) =>
                price.promotype === SalesforceProductPromotionType.MEMBER_PRICE,
        );

        // Member price
        if (isPlusMember && !!memberPrice) return memberPrice.value;

        // Default, Sets, Multi-buy, and Mix & Match prices
        return product.price;
    }, [product, isPlusMember]);

    const meetsPriceThreshold =
        ascertainNumber(assumedPrice, { parser: parseFloat }) >=
        (siteSettings?.klarnaMinimumAmountThreshold ?? 0);

    const shouldShowKlarnaIcon =
        product.c_in_stock_web &&
        productSettings?.klarnaBadgeEnabled &&
        isKlarnaInPaymentOptions &&
        meetsPriceThreshold;

    return (
        <div className={clsx(styles.paymentMethods, className)}>
            <PaymentMethodIcon src="/assets/payment/icon-apple-pay.png" name="Apple Pay" />
            <PaymentMethodIcon src="/assets/payment/icon-mobile-pay.png" name="MobilePay" />
            <PaymentMethodIcon src="/assets/payment/icon-dankort.png" name="Dankort" />
            <PaymentMethodIcon src="/assets/payment/icon-visa.png" name="Visa" />
            <PaymentMethodIcon src="/assets/payment/icon-mastercard.png" name="Mastercard" />

            {shouldShowKlarnaIcon && (
                <PaymentMethodIcon src="/assets/payment/icon-klarna.png" name="Klarna" />
            )}
        </div>
    );
};
